/**
 * Phone number utilities for UK phone number validation and formatting
 */

/**
 * Formats a UK phone number to E.164 international format (+44...)
 * Automatically converts 07... to +447...
 */
export function formatPhoneToE164(phone: string): string {
  if (!phone) return phone;
  
  // Remove all non-digit characters except leading +
  let cleaned = phone.replace(/[^\d+]/g, '');
  
  // If it starts with +, assume it's already in international format
  if (cleaned.startsWith('+')) {
    return cleaned;
  }
  
  // UK number handling
  if (cleaned.startsWith('0')) {
    // Replace leading 0 with +44 for UK numbers
    cleaned = '+44' + cleaned.substring(1);
  } else if (cleaned.startsWith('44')) {
    // Add + prefix if missing
    cleaned = '+' + cleaned;
  } else if (cleaned.length === 10 || cleaned.length === 11) {
    // Assume UK number without prefix
    cleaned = '+44' + cleaned;
  }
  
  return cleaned;
}

/**
 * Validates if a phone number is valid (not empty, placeholder, or too short)
 */
export function isValidPhoneNumber(phone: string | null | undefined): boolean {
  if (!phone) return false;
  
  const trimmed = phone.trim().toLowerCase();
  
  // Check for common placeholder values
  if (trimmed === '' || trimmed === 'n/a' || trimmed === 'na' || trimmed === 'none' || trimmed === '-' || trimmed === 'tbd') {
    return false;
  }
  
  // Check if it has at least some digits (minimum 10 for a valid UK phone)
  const digitsOnly = phone.replace(/\D/g, '');
  return digitsOnly.length >= 10;
}

/**
 * True when the value is empty or a known placeholder ("N/A", "NA", "none",
 * "-", "TBD") rather than a real phone number.
 */
export function isPlaceholderPhone(phone: string | null | undefined): boolean {
  if (!phone) return true;
  const trimmed = phone.trim().toLowerCase();
  return trimmed === '' || trimmed === 'n/a' || trimmed === 'na' || trimmed === 'none' || trimmed === '-' || trimmed === 'tbd';
}

/**
 * Extracts a callable phone candidate from a free-text field (e.g. a job
 * contact stored as "Name - Phone - Email"). Returns null when no
 * phone-shaped substring exists.
 */
export function extractPhoneCandidate(value: string | null | undefined): string | null {
  if (!value) return null;
  const candidate = value.match(/[\d\s+()-]{10,}/)?.[0] ?? null;
  // Strip leading separators such as " - " from "Name - Phone - Email".
  return candidate?.replace(/^[^\d+]+/, "").replace(/[\s()-]+$/, "").trim() || null;
}

/**
 * Resolves the number the Call Collection button should dial.
 *
 * Priority:
 *   1. A valid customer record phone.
 *   2. A valid phone on the job's collection/delivery contact field.
 *
 * Placeholder and invalid values are rejected by the existing
 * isValidPhoneNumber() rules — this never weakens validation.
 */
export function resolveCollectionCallPhone(
  customerPhone: string | null | undefined,
  jobContactPhone: string | null | undefined,
): string | null {
  const customerCandidate = extractPhoneCandidate(customerPhone);
  if (isValidPhoneNumber(customerCandidate)) return customerCandidate;
  const jobCandidate = extractPhoneCandidate(jobContactPhone);
  if (isValidPhoneNumber(jobCandidate)) return jobCandidate;
  return null;
}

/**
 * Formats phone for display (keeps it readable but ensures proper format)
 */
export function formatPhoneForStorage(phone: string): string {
  if (!phone) return phone;
  
  // Always store in E.164 format for consistency
  return formatPhoneToE164(phone.trim());
}

/**
 * Zod refine function for phone validation
 */
export function zodPhoneValidation(phone: string): boolean {
  return isValidPhoneNumber(phone);
}

/**
 * Error message for invalid phone numbers
 */
export const PHONE_VALIDATION_ERROR = "Please enter a valid UK phone number (e.g., 07123456789 or +447123456789)";
